// src/admin/pages/CurrencyManagementPage.js
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, DollarSign, Globe, Eye, EyeOff, TrendingUp, RefreshCw } from 'lucide-react';
import CurrencyService from '../../services/currencyService';

const CurrencyManagementPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [refreshingRates, setRefreshingRates] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Initialize and refresh exchange rates
  useEffect(() => {
    const loadRates = async () => {
      setRefreshingRates(true);
      try {
        await CurrencyService.initializeExchangeRates();
        setLastUpdated(new Date());
      } catch (error) {
        console.error('Failed to load exchange rates:', error);
      } finally {
        setRefreshingRates(false);
      }
    };
    loadRates();
  }, []);

  const handleRefreshRates = async () => {
    setRefreshingRates(true);
    try {
      CurrencyService.clearExchangeRateCache();
      await CurrencyService.getExchangeRates();
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to refresh exchange rates:', error);
    } finally {
      setRefreshingRates(false);
    }
  };

  // Expanded currency list with the requested currencies
  const defaultCurrencies = [
    // Major Global Currencies
    { code: 'USD', name: 'US Dollar', symbol: '$', flag: '🇺🇸', region: 'Americas', isActive: true },
    { code: 'EUR', name: 'Euro', symbol: '€', flag: '🇪🇺', region: 'Europe', isActive: true },
    { code: 'GBP', name: 'British Pound', symbol: '£', flag: '🇬🇧', region: 'Europe', isActive: true },
    
    // Asia-Pacific
    { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', flag: '🇸🇬', region: 'Asia-Pacific', isActive: true },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', flag: '🇦🇺', region: 'Asia-Pacific', isActive: true },
    { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', flag: '🇳🇿', region: 'Asia-Pacific', isActive: true },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥', flag: '🇯🇵', region: 'Asia-Pacific', isActive: true },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', flag: '🇨🇳', region: 'Asia-Pacific', isActive: true },
    { code: 'KRW', name: 'Korean Won', symbol: '₩', flag: '🇰🇷', region: 'Asia-Pacific', isActive: true },
    { code: 'VND', name: 'Vietnamese Dong', symbol: '₫', flag: '🇻🇳', region: 'Asia-Pacific', isActive: true },
    
    // Europe
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', flag: '🇨🇭', region: 'Europe', isActive: true },
    
    // Americas
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', flag: '🇨🇦', region: 'Americas', isActive: true },
    { code: 'MXN', name: 'Mexican Peso', symbol: '$', flag: '🇲🇽', region: 'Americas', isActive: false },
    { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', flag: '🇧🇷', region: 'Americas', isActive: false },
    
    // Middle East & Africa
    { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', flag: '🇦🇪', region: 'Middle East', isActive: false },
    { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼', flag: '🇸🇦', region: 'Middle East', isActive: false },
    { code: 'ZAR', name: 'South African Rand', symbol: 'R', flag: '🇿🇦', region: 'Africa', isActive: false }
  ];

  const regions = ['All Regions', 'Asia-Pacific', 'Europe', 'Americas', 'Middle East', 'Africa'];

  const currencyColumns = [
    { key: 'code', label: 'Code', width: 'w-20' },
    { key: 'flag', label: 'Flag', width: 'w-16' },
    { key: 'name', label: 'Currency Name', width: 'w-40' },
    { key: 'symbol', label: 'Symbol', width: 'w-20' },
    { key: 'exchangeRate', label: 'Exchange Rate (vs USD)', width: 'w-32' },
    { key: 'region', label: 'Region', width: 'w-32' },
    { key: 'isActive', label: 'Status', width: 'w-24' },
    { key: 'actions', label: 'Actions', width: 'w-32' }
  ];

  const [currencies, setCurrencies] = useState(defaultCurrencies);

  const filteredCurrencies = currencies.filter(currency => {
    const matchesSearch = currency.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         currency.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = !selectedRegion || selectedRegion === 'All Regions' || currency.region === selectedRegion;
    return matchesSearch && matchesRegion;
  });

  const handleToggleActive = (currencyCode) => {
    setCurrencies(prev => prev.map(currency => 
      currency.code === currencyCode 
        ? { ...currency, isActive: !currency.isActive }
        : currency
    ));
  };

  const handleDelete = (currencyCode) => {
    if (window.confirm(`Are you sure you want to delete ${currencyCode}?`)) {
      setCurrencies(prev => prev.filter(currency => currency.code !== currencyCode));
    }
  };

  const handleEdit = (currency) => {
    setEditingCurrency(currency);
    setShowAddModal(true);
  };

  const handleSaveCurrency = (currencyData) => {
    if (editingCurrency) {
      // Update existing currency
      setCurrencies(prev => prev.map(currency => 
        currency.code === editingCurrency.code 
          ? { ...currency, ...currencyData }
          : currency
      ));
      setEditingCurrency(null);
    } else {
      // Add new currency
      setCurrencies(prev => [...prev, { ...currencyData, isActive: true }]);
    }
    setShowAddModal(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Currency Management</h1>
          <p className="text-gray-600">Manage supported currencies and their properties</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Currency
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <DollarSign className="w-8 h-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Currencies</p>
              <p className="text-2xl font-semibold text-gray-900">{currencies.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <Eye className="w-8 h-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Active Currencies</p>
              <p className="text-2xl font-semibold text-gray-900">
                {currencies.filter(c => c.isActive).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <EyeOff className="w-8 h-8 text-red-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Inactive Currencies</p>
              <p className="text-2xl font-semibold text-gray-900">
                {currencies.filter(c => !c.isActive).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <Globe className="w-8 h-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Regions</p>
              <p className="text-2xl font-semibold text-gray-900">{regions.length - 1}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Exchange Rates Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start flex-1">
            <TrendingUp className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-blue-900">Real-Time Exchange Rates</h3>
              <p className="text-sm text-blue-700 mt-1">
                Exchange rates are fetched from <strong>ExchangeRate-API.com</strong> (free tier, no API key required).
                <br />
                <span className="text-xs text-blue-600 mt-1 block">
                  API Endpoint: <code className="bg-blue-100 px-1 rounded">https://open.er-api.com/v6/latest/USD</code>
                  <br />
                  Fallback: <code className="bg-blue-100 px-1 rounded">https://api.exchangerate-api.com/v4/latest/USD</code>
                </span>
                Rates are cached for 1 hour to minimize API calls.
                {lastUpdated && (
                  <span className="block mt-1 text-xs text-blue-600">
                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={handleRefreshRates}
            disabled={refreshingRates}
            className="ml-4 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm"
            title="Refresh exchange rates"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshingRates ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex items-center space-x-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search currencies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select 
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {regions.map(region => (
              <option key={region} value={region}>{region}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Currency Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {currencyColumns.map(column => (
                  <th key={column.key} className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${column.width}`}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCurrencies.map(currency => (
                <tr key={currency.code} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {currency.code}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-2xl">
                    {currency.flag}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {currency.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {currency.symbol}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {currency.code === 'USD' ? (
                      <span className="text-blue-600 font-semibold">1.0000 (Base)</span>
                    ) : (
                      <span className="flex items-center">
                        <TrendingUp className="w-3 h-3 mr-1 text-green-600" />
                        {CurrencyService.getExchangeRateToUSDSync(currency.code)?.toFixed(4) || 'N/A'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {currency.region}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleActive(currency.code)}
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full transition-colors ${
                        currency.isActive 
                          ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      }`}
                    >
                      {currency.isActive ? (
                        <>
                          <Eye className="w-3 h-3 mr-1" />
                          Active
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-3 h-3 mr-1" />
                          Inactive
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(currency)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit currency"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(currency.code)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete currency"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredCurrencies.length === 0 && (
          <div className="text-center py-8">
            <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No currencies found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedRegion ? 'Try adjusting your search or filter criteria.' : 'Get started by adding a new currency.'}
            </p>
          </div>
        )}
      </div>

      {/* Add/Edit Currency Modal */}
      {showAddModal && (
        <CurrencyModal
          currency={editingCurrency}
          onSave={handleSaveCurrency}
          onClose={() => {
            setShowAddModal(false);
            setEditingCurrency(null);
          }}
        />
      )}
    </div>
  );
};

// Currency Modal Component
const CurrencyModal = ({ currency, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    code: currency?.code || '',
    name: currency?.name || '',
    symbol: currency?.symbol || '',
    flag: currency?.flag || '',
    region: currency?.region || 'Americas',
    isActive: currency?.isActive ?? true
  });

  const regions = ['Americas', 'Asia-Pacific', 'Europe', 'Middle East', 'Africa'];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {currency ? 'Edit Currency' : 'Add New Currency'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Currency Code *
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                placeholder="USD"
                maxLength="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
                disabled={!!currency}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Currency Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="US Dollar"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Symbol *
              </label>
              <input
                type="text"
                value={formData.symbol}
                onChange={(e) => handleChange('symbol', e.target.value)}
                placeholder="$"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Flag Emoji
              </label>
              <input
                type="text"
                value={formData.flag}
                onChange={(e) => handleChange('flag', e.target.value)}
                placeholder="🇺🇸"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Region *
              </label>
              <select
                value={formData.region}
                onChange={(e) => handleChange('region', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                {regions.map(region => (
                  <option key={region} value={region}>{region}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                Active currency
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                {currency ? 'Update' : 'Add'} Currency
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CurrencyManagementPage;
